import { Component } from '@verndale/core';
import { isLargeDesktop, debounce } from '../../helpers';

class Header extends Component {
  lastScrollTop: number;
  outsideClickListener: EventListenerOrEventListenerObject;
  isLargeDesktop: boolean;
  bodyScrollTop: number;
  alertBannerClosed: boolean;
  handleOpenedNavigationKeyDownBound: EventListenerOrEventListenerObject;

  constructor(el: HTMLElement) {
    super(el);
    this.lastScrollTop = 0;
    this.outsideClickListener = this.handleOutsideClick.bind(this);
    this.isLargeDesktop = isLargeDesktop();
    this.bodyScrollTop = 0;
    this.alertBannerClosed = false;
    this.handleOpenedNavigationKeyDownBound = this.handleOpenedNaviationKeyDown.bind(this) as EventListenerOrEventListenerObject;
  }

  setupDefaults() {
    this.dom = {
      alertBanner: this.el.querySelector<HTMLElement>('.alert-banner'),
      closeAlertBanner: this.el.querySelector<HTMLElement>('.alert-banner__close-button'),
      hamburguerButton: this.el.querySelector<HTMLElement>('.global-navigation__hamburguer-button'),
      closeMenuButton: this.el.querySelector<HTMLElement>('.global-navigation__close-button'),
      utilityNavigation: this.el.querySelector<HTMLElement>('.utility-navigation'),
      searchEraseButton: this.el.querySelectorAll<HTMLElement>('button.search-menu__erase-button')[1],
      navigationWrapper: this.el.querySelector<HTMLElement>('.global-navigation__menu-wrapper'),
      navigationMenus: this.el.querySelectorAll<HTMLElement>('.global-navigation__menu'),
      navigationPrimaryItemsContainer: this.el.querySelector<HTMLElement>('.global-navigation__items'),
      navigationPrimaryItems: this.el.querySelectorAll<HTMLElement>('.global-navigation__items__navigation-item'),
      navigationPrimaryTriggers: this.el.querySelectorAll<HTMLElement>('.global-navigation__items__navigation-button'),
      navigationSecondaryTriggers: this.el.querySelectorAll<HTMLElement>('.global-navigation__menu-column__navigation-button'),
      backToMenuTriggers: this.el.querySelectorAll<HTMLElement>('.global-navigation__menu__back-button'),
      backToSecondaryMenuTriggers: this.el.querySelectorAll<HTMLElement>('.global-navigation__menu-column__back-button'),
      navigationPrimaryLinks: this.el.querySelectorAll<HTMLElement>('.global-navigation__menu__item-link'),
      navigationPrimaryButtons: this.el.querySelectorAll<HTMLElement>('.global-navigation__items__navigation-button'),
      navigationSecondaryLinks: this.el.querySelectorAll<HTMLElement>('.global-navigation__items__navigation-link'),
    };

    this.checkMobileNavigationVisibility();
    this.checkAlertBanner();
  }

  addListeners() {
    (this.dom.hamburguerButton as HTMLElement).addEventListener('click', this.openMobileNavigation.bind(this));
    (this.dom.closeMenuButton as HTMLElement).addEventListener('click', this.closeMobileNavigation.bind(this));
    
    if (this.dom.closeAlertBanner) {
      (this.dom.closeAlertBanner as HTMLElement).addEventListener('click', this.closeAllertBanner.bind(this));
    }

    Array.from(this.dom.navigationSecondaryLinks as NodeListOf<HTMLElement>).forEach((navigationSecondaryLink) => {
      navigationSecondaryLink.addEventListener('keydown', this.handleSecondaryLinkKeydown.bind(this) as EventListener);
    });

    Array.from(this.dom.navigationPrimaryLinks as NodeListOf<HTMLElement>).forEach((navigationPrimaryLink) => {
      navigationPrimaryLink.addEventListener('keydown', this.handleSecondaryLinkKeydown.bind(this) as EventListener);
    });

    Array.from(this.dom.navigationPrimaryButtons as NodeListOf<HTMLElement>).forEach((navigationPrimaryButton) => {
      navigationPrimaryButton.addEventListener('keydown', this.handleSecondaryLinkKeydown.bind(this) as EventListener);
    });

    window.addEventListener('scroll', this.handleUtilityNavigationVisibility.bind(this));

    Array.from(this.dom.navigationPrimaryTriggers as NodeListOf<HTMLElement>).forEach((navigationPrimaryTrigger) => {
      navigationPrimaryTrigger.addEventListener('click', this.handleSecondaryMenu.bind(this));
    });

    Array.from(this.dom.navigationSecondaryTriggers as NodeListOf<HTMLElement>).forEach((navigationSecondaryTrigger) => {
      navigationSecondaryTrigger.addEventListener('click', this.handleTertiaryMenu.bind(this));
    });

    Array.from(this.dom.backToMenuTriggers as NodeListOf<HTMLElement>).forEach((backToMenuTrigger) => {
      backToMenuTrigger.addEventListener('click', this.handleBackToMenu.bind(this));
    });

    Array.from(this.dom.backToSecondaryMenuTriggers as NodeListOf<HTMLElement>).forEach((backToSecondaryMenuTrigger) => {
      backToSecondaryMenuTrigger.addEventListener('click', this.handleBackToSecondaryMenu.bind(this));
    });

    window.addEventListener(
      'resize',
      debounce(this.checkViewportWidth.bind(this), 300)
    );
  }

  checkViewportWidth() {
    this.isLargeDesktop = isLargeDesktop();
    this.checkAlertBanner();
  }

  handleSecondaryLinkKeydown(event: KeyboardEvent) {
    const activeItem = (event.target as HTMLElement).closest('.global-navigation__items__navigation-item') as HTMLElement;

    if (!activeItem) {
      return;
    }

    const prevItem = activeItem.previousElementSibling as HTMLElement;
    const prevFocusable = prevItem && (prevItem.querySelector('.global-navigation__items__navigation-button') as HTMLElement || prevItem.querySelector('.global-navigation__items__navigation-link') as HTMLElement);
    const nextItem = activeItem.nextElementSibling as HTMLElement;
    const nextFocusable = nextItem && (nextItem.querySelector('.global-navigation__items__navigation-button') as HTMLElement || nextItem.querySelector('.global-navigation__items__navigation-link') as HTMLElement);

    switch (event.key) {
      case 'ArrowLeft':
        if (prevFocusable) {
          prevFocusable.focus();
        }
        event.preventDefault();
        break;

      case 'ArrowRight':
        if (nextFocusable) {
          nextFocusable.focus();
        }
        event.preventDefault();
        break;

      default:
        break;
    }
  }

  checkAlertBanner() {
    if (this.alertBannerClosed) return;

    if (this.dom.alertBanner) {
      const alertBanner = this.dom.alertBanner as HTMLElement;
      const allertBannerHeight = (alertBanner.querySelector('.container') as HTMLElement).offsetHeight;

      this.el.style.insetBlockStart = `-${allertBannerHeight}px`;
      alertBanner.style.blockSize = `${allertBannerHeight}px`;
    }
  }

  closeAllertBanner() {
    const alertBanner = this.dom.alertBanner as HTMLElement;
    const allertBannerHeight = alertBanner.offsetHeight;
    this.alertBannerClosed = true;
    
    alertBanner.style.insetBlockStart = `-${allertBannerHeight}px`;
    alertBanner.style.blockSize = `0`;

    if (!isLargeDesktop()) {
      this.el.style.insetBlockStart = `0`;
    }

    Array.from(alertBanner.querySelectorAll('a, button')).forEach((link) => {
      link.setAttribute('tabindex', '0');
    });
  }

  showUtilityNavigation() {
    const alertBanner = this.dom.alertBanner as HTMLElement;
    const allertBannerHeight = alertBanner?.offsetHeight || 0;
    const utilityNavigation = this.dom.utilityNavigation as HTMLElement;

    utilityNavigation.setAttribute('aria-hidden', 'false');
    this.el.style.insetBlockStart = `-${allertBannerHeight}px`;


    Array.from(utilityNavigation.querySelectorAll('a, button')).forEach((link) => {
      link.setAttribute('tabindex', '0');
    });
  }

  hideUtilityNavigation() {
    const utilityNavigation = this.dom.utilityNavigation as HTMLElement;
    const utilityNavigationHeight = utilityNavigation.offsetHeight;
    const alertBanner = this.dom.alertBanner as HTMLElement;
    const allertBannerHeight = alertBanner?.offsetHeight || 0;

    utilityNavigation.setAttribute('aria-hidden', 'true');
    this.el.style.insetBlockStart = `-${utilityNavigationHeight + allertBannerHeight}px`;

    Array.from(utilityNavigation.querySelectorAll('a, button')).forEach((link) => {
      link.setAttribute('tabindex', '-1');
    });
  }

  handleUtilityNavigationVisibility() {
    const currentScroll = window.scrollY || document.documentElement.scrollTop;

    const alertBanner = this.dom.alertBanner as HTMLElement;
    const allertBannerHeight = alertBanner?.offsetHeight || 0;
    const isBelowAlertBanner = currentScroll > allertBannerHeight;

    if (isBelowAlertBanner) {
      if (currentScroll > this.lastScrollTop) {
        this.hideUtilityNavigation();
      } else {
        this.showUtilityNavigation();
      }
    }

    this.lastScrollTop = currentScroll;
  }

  closeAllMenus() {
    document.removeEventListener('keydown', this.handleOpenedNavigationKeyDownBound);

    Array.from(this.dom.navigationMenus as NodeListOf<HTMLElement>).forEach((navigationMenu) => {
      navigationMenu.setAttribute('aria-hidden', 'true');
      Array.from(navigationMenu.querySelectorAll('a, button')).forEach((link) => {
        link.setAttribute('tabindex', '-1');
      });
      const dropdownInner = navigationMenu.querySelector('.global-navigation__menu-inner') as HTMLElement;
      dropdownInner.style.transform = `none`;
    });

    Array.from(this.dom.navigationPrimaryTriggers as NodeListOf<HTMLElement>).forEach((navigationItem) => {
      navigationItem.setAttribute('aria-expanded', 'false');
    });

    Array.from(this.dom.navigationPrimaryItems as NodeListOf<HTMLElement>).forEach((navigationItem) => {
      navigationItem.classList.remove('global-navigation__items__navigation-item--active');
      (this.dom.navigationPrimaryItemsContainer as HTMLElement).classList.remove('global-navigation__items--hidden');
    });


    if (isLargeDesktop()) {
      document.removeEventListener('click', this.outsideClickListener);
    }
  }

  openMenu(menuItem: HTMLElement, dropdown: HTMLElement) {
    const trigger = menuItem.querySelector('.global-navigation__items__navigation-button') as HTMLElement;
    trigger.setAttribute('aria-expanded', 'true');

    document.addEventListener('keydown', this.handleOpenedNavigationKeyDownBound);

    menuItem.classList.add('global-navigation__items__navigation-item--active');
    (this.dom.navigationPrimaryItemsContainer as HTMLElement).classList.add('global-navigation__items--hidden');
    const dropdownInner = dropdown.querySelector('.global-navigation__menu-inner') as HTMLElement;

    const diff = this.getWidthDifference(dropdownInner);

    if (diff < 0 && isLargeDesktop()) {
      dropdownInner.style.transform = `translateX(${diff}px)`;
    }

    dropdown.setAttribute('aria-hidden', 'false');
    Array.from(dropdown.querySelectorAll('a, button')).forEach((link) => {
      link.setAttribute('tabindex', '0');
    });

    if (isLargeDesktop()) {
      document.addEventListener('click', this.outsideClickListener);
    }
  }

  checkMobileNavigationVisibility() {
    if (!isLargeDesktop()) {
      (this.dom.navigationWrapper as HTMLElement).setAttribute('aria-hidden', 'true');
    }

    this.closeAllMenus();
  }

  lockScroll() {
    setTimeout(() => {
      if (!this.isLargeDesktop) {
        this.bodyScrollTop = window.scrollY || document.documentElement.scrollTop;
        document.body.style.position = 'fixed';
        document.body.style.top = `-${this.bodyScrollTop}px`;
        document.body.style.width = '100%';
      }
    }, 300);
  }

  unlockScroll() {
    if (!this.isLargeDesktop) {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo({ top: this.bodyScrollTop, left: 0, behavior: 'instant' });
    }
  }

  openMobileNavigation() {
    this.lockScroll();

    this.el.classList.add('header--navigation-opened');
    (this.dom.navigationWrapper as HTMLElement).setAttribute('aria-hidden', 'false');
  }

  closeMobileNavigation() {    
    (this.dom.searchEraseButton as HTMLInputElement).click();

    this.el.classList.remove('header--navigation-opened');
    this.unlockScroll();
    (this.dom.navigationWrapper as HTMLElement).setAttribute('aria-hidden', 'true');
    this.closeAllMenus();
  }

  handleOpenedNaviationKeyDown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      this.closeAllMenus();
    }
  }

  handleSecondaryMenu(ev: Event) {
    const menuItem = (ev.currentTarget as HTMLElement).parentElement as HTMLElement;
    const dropdown = (ev.currentTarget as HTMLElement).nextElementSibling as HTMLElement;

    if (menuItem.classList.contains('global-navigation__items__navigation-item--active')) {
      this.closeAllMenus();
      return;
    }

    this.closeAllMenus();
    this.openMenu(menuItem, dropdown);
  }

  handleBackToMenu() {
    this.closeAllMenus();
  }

  handleBackToSecondaryMenu(ev: Event) {
    const menuItem = (ev.currentTarget as HTMLElement).closest('.global-navigation__menu-column') as HTMLElement;
    const secondary = menuItem.closest('.global-navigation__items__navigation-item') as HTMLElement;
    secondary.classList.remove('global-navigation__items__navigation-item--hidden');    
    menuItem.classList.remove('--active');
  }

  openTertiaryMenu(menuItem: HTMLElement) {
    const secondary = menuItem.closest('.global-navigation__items__navigation-item') as HTMLElement;
    secondary.classList.add('global-navigation__items__navigation-item--hidden');    
    menuItem.classList.add('--active');
  }

  handleTertiaryMenu(ev: Event) {
    const menuItem = (ev.currentTarget as HTMLElement).parentElement as HTMLElement;
    this.openTertiaryMenu(menuItem);
  }

  closeDropdowns() {
    this.el.classList.remove('header--navigation-opened');
    this.unlockScroll();
    (this.dom.navigationWrapper as HTMLElement).setAttribute('aria-hidden', 'true');
    this.closeAllMenus();
  }

  handleOutsideClick(event: Event) {
    const header = this.el.querySelector('.global-navigation .container') as HTMLElement;
    const isClickInside = header.contains(event.target as Node);
    if (!isClickInside) {
      this.closeDropdowns();
    }
  }

  getWidthDifference(menu: HTMLElement) {
    const header = this.el.querySelector('.global-navigation .container') as HTMLElement;
    const menuRight = menu.getBoundingClientRect().right;
    const navRight = header.getBoundingClientRect().right - 48;

    return navRight - menuRight;
  }
}

export default Header;